import { ReferenceNumberField } from "@/services/backend/documents/components/reference-number-field";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  PatchRequest,
  updateHtzSummaryCache,
  useHtzSummaryPatchMutation,
  useHtzSummaryResetReferenceNumberMutation,
  useHtzSummaryShowQuery,
} from "@/services/backend/htz/summary/api/summary-api";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { TextInputField } from "@/shared/components/field/input-field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { CpySelectField } from "@/shared/components/field/cpy-select-field";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { isZeroUUID } from "@/shared/lib/utilities/uuid";

export function SummaryDataCard({
  id,
  disabled,
}: {
  id: UUID;
  disabled?: boolean;
}) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useHtzSummaryShowQuery({ id });

  const request: PatchRequest = { id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzSummaryCache,
    useHtzSummaryPatchMutation(),
  );
  const patchReferenceNumber = useHtzSummaryPatchMutation();
  const resetReferenceNumber = useHtzSummaryResetReferenceNumberMutation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Inspektionsbericht")}</CardTitle>
        <CardDescription>
          {t("Stammdaten zum Inspektionsbericht")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {dataError && <RTKQueryErrorAlert error={dataError} />}
        {dataLoading && <CardContentSkeleton />}
        {data && (
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-6 lg:col-span-3">
              <ReferenceNumberField
                data={data}
                updateCache={updateHtzSummaryCache}
                patchMutation={patchReferenceNumber}
                resetMutation={resetReferenceNumber}
                disabled={disabled || isImmutable(data)}
              />
            </div>
            <CpySelectField
              label={t("Arbeitsschein")}
              value={toCpyValue(data.workOrderId)}
              placeholder={t("-")}
              warning=""
              destinationId={data.id}
              destinationEntityName={CpyName.HTZSummaryReport}
              sourceEntityName={CpyName.HTZWorkOrder}
              sourcesParams={{
                process: { active: true, values: [data.processId!] },
              }}
              fieldProps={{ className: "col-span-6 lg:col-span-3" }}
              disabled
            />
            <TextInputField
              label={t("Titel")}
              text={data.title}
              onTextChange={(title) => {
                set({ ...request, title }, { ...data, title });
              }}
              fieldName="title"
              fieldProps={{ className: "col-span-4" }}
              disabled={disabled}
            />
            <DatePickerField
              label={t("Datum")}
              nullable
              date={data.date ?? undefined}
              onDateChange={(date) => {
                set({ ...request, date: date ?? null }, { ...data, date });
              }}
              fieldName="date"
              fieldProps={{ className: "col-span-2" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Untertitel")}
              text={data.subtitle}
              onTextChange={(subtitle) => {
                set({ ...request, subtitle }, { ...data, subtitle });
              }}
              fieldName="subtitle"
              fieldProps={{ className: "col-span-6" }}
              disabled={disabled}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// toCpyValue maps an unset reference to no selection, so the select shows
// its placeholder.
function toCpyValue(id: string | null | undefined): string | undefined {
  return !id || isZeroUUID(id) ? undefined : id;
}
